import { DomainCheckResponseData, DomainCheckResults } from '@/core/ryanada'
import { DomainHTTP } from '@/http'

declare const BULK_DOMAIN_MAX_REQUESTS: string

export class BulkDomainSearchViewModel {
  private phraseList: string[] = []
  private tldList: string[] = []
  private http: DomainHTTP
  private readonly TOKEN: string

  constructor(http: DomainHTTP, token: string) {
    this.http = http
    this.TOKEN = token
  }

  public get canSubmit(): boolean {
    return (
      this.phraseList.length > 0 &&
      this.tldList.length > 0 &&
      this.isNumberOfRequestsValid
    )
  }

  private get isNumberOfRequestsValid(): boolean {
    const numberOfRequests = this.phraseList.length * this.tldList.length
    return numberOfRequests < Number.parseInt(BULK_DOMAIN_MAX_REQUESTS)
  }

  public get validationMessage(): string | null {
    if (this.phraseList.length < 1) {
      return 'Please add at least one domain name.'
    }

    if (this.tldList.length < 1) {
      return 'Please add at least one tld.'
    }

    if (!this.isNumberOfRequestsValid) {
      return `Too many requests: Maximum of ${BULK_DOMAIN_MAX_REQUESTS} requests allowed. Please reduce number of domains or tlds`
    }

    return null
  }

  public containsTld(tld: string): boolean {
    return this.tldList.includes(tld)
  }

  public addTld(tld: string): void {
    this.tldList.push(tld)
  }

  public removeTld(tld: string): void {
    this.tldList = this.tldList.filter((_tld) => _tld !== tld)
  }

  public updatePhraseList(list: string[]): void {
    this.phraseList = list
  }

  public async search(): Promise<DomainCheckResults[]> {
    const searches: Promise<DomainCheckResponseData>[] = []

    for (const phrase of this.phraseList) {
      for (const tld of this.tldList) {
        const formData = new FormData()
        formData.append('token', this.TOKEN)
        formData.append('a', 'checkDomain')
        formData.append('domain', phrase)
        formData.append('sld', phrase)
        formData.append('tld', tld)
        formData.append('type', 'domain')
        searches.push(this.http.checkDomain(formData))
      }
    }

    try {
      const responses = await Promise.allSettled(searches)
      const domains = responses
        .map((resp) => {
          if (resp.status === 'fulfilled') {
            return resp.value.result
          } else {
            return []
          }
        })
        .flat()
      return domains
    } catch (error) {
      return []
    }
  }
}
